import type { Dict } from './de'

/** English dictionary — structure enforced by the type of the German dict. */
export const en: Dict = {
    app: {
        title: 'Optimus Time — Time Tracking',
    },
    landing: {
        hero_title: 'Time tracking that keeps up with you',
        hero_subtitle:
            'Capture every hour, bill accurately and see where your time really goes — for you and your whole team.',
        beta_banner:
            'Optimus Time is in beta and not yet intended for production use. Features may still change — please don’t rely on it as your sole record for billable hours.',
        cta_sign_up: 'Start for free',
        cta_sign_in: 'Sign in',
        feature_timer_title: 'One-click timer',
        feature_timer_text:
            'Start, stop and edit time entries in seconds — at your desk or on the go.',
        feature_reports_title: 'Reports & CSV export',
        feature_reports_text:
            'Group by project, client, member or day and export everything as CSV.',
        feature_teams_title: 'Workspaces & teams',
        feature_teams_text:
            'Invite colleagues, assign roles and keep clients and projects organized.',
        feature_billing_title: 'Billable hours',
        feature_billing_text:
            'Mark entries as billable, set hourly rates and see your earnings at a glance.',
    },
    common: {
        save: 'Save',
        cancel: 'Cancel',
        close: 'Close',
        delete: 'Delete',
        edit: 'Edit',
        create: 'Create',
        all: 'All',
        loading: 'Loading',
        back: '← Back',
        next: 'Next →',
        exportFailed: 'Export failed',
        openMenu: 'Open menu',
        closeMenu: 'Close menu',
        beta: 'Beta',
        hour: 'Hour',
        minute: 'Minute',
        pleaseConfirm: 'Please confirm',
        confirm: 'Confirm',
    },
    nav: {
        tracking: 'Time tracking',
        billing: 'Billing',
        entries: 'Entries',
        reports: 'Reports',
        invoices: 'Invoices',
        monthly: 'Monthly hours',
        workspace: 'Workspace',
        settings: 'Settings',
        admin: 'Admin',
        logout: 'Sign out',
        mainNav: 'Main navigation',
        userMenu: 'Open user menu',
        workspaceSwitch: 'Switch workspace',
        language: 'Language',
        feedback: 'Give feedback',
    },
    timer: {
        stop: 'Stop timer',
        stopped: 'Timer stopped',
        started: 'Timer started: {type}',
        switched: 'Switched to {type}',
        ready: 'Timer ready',
        runningSince: '{type} running since {time}',
        stopType: 'Stop {type}',
        switchTo: 'Switch to {type}',
        startType: 'Start {type}',
        goToTracking: 'Go to time tracking',
    },
    entryType: {
        work: 'Work',
        break: 'Break',
        meeting: 'Meeting',
        admin: 'Admin',
        sick: 'Sick',
        vacation: 'Vacation',
        timeOff: 'Time off',
        paidAbsence: 'Paid absence',
    },
    invoiceStatus: {
        draft: 'Draft',
        sent: 'Sent',
        paid: 'Paid',
        cancelled: 'Cancelled',
    },
    track: {
        today: 'Today',
        thisWeek: 'This week',
        summaryLine: '{billable} billable · {money} · {count} entries',
        manualTitle: 'Add time manually',
        hide: 'Hide',
        add: '+ Add',
        descriptionPlaceholder: 'What are you working on?',
        manualDescriptionPlaceholder: 'Short description of the activity',
        projectClient: 'Project / Client',
        type: 'Type',
        billable: 'Billable',
        start: 'Start',
        end: 'End',
        endBeforeStart: 'End must not be before start.',
        entrySaved: 'Entry saved',
        saveEntry: 'Save entry',
        description: 'Description',
    },
    projectSelect: {
        label: 'Project / client',
        withoutAssignment: '— unassigned —',
        withoutProject: '{name} (no project)',
    },
    entries: {
        project: 'Project',
        client: 'Client',
        from: 'From',
        to: 'To',
        csvExport: '↓ CSV export',
        csvName: 'time-report.csv',
        emptyTitle: 'No time entries',
        emptyDesc:
            'Adjust the filters or track time on the time tracking page.',
        date: 'Date',
        duration: 'Duration',
        yield: 'Revenue',
        running: 'running',
        open: 'open',
        actions: 'Actions',
        pageInfo: 'Page {page} of {totalPages} · {count} entries',
        editTitle: 'Edit time entry',
        createTitle: 'Create time entry',
        deleteConfirm: 'Really delete this time entry?',
        entryDeleted: 'Entry deleted',
        entryUpdated: 'Entry updated',
        endRunningHint: 'End (empty = running)',
    },
    reports: {
        period: 'Period',
        presetWeek: 'This week',
        presetMonth: 'This month',
        presetYear: 'This year',
        presetAll: 'All time',
        presetCustom: 'Custom range',
        groupBy: 'Group by',
        groupProject: 'Project',
        groupClient: 'Client',
        groupUser: 'Person',
        groupDay: 'Day',
        total: 'Total',
        billable: 'Billable',
        earnings: 'Revenue',
        entriesLabel: 'Entries',
        runningOne: 'One timer is currently running and included in the report.',
        runningMany:
            '{count} timers are currently running and included in the report.',
        note: 'Note',
        groupingTitle: 'Grouping: {group}',
        loadErrorTitle: 'Report could not be loaded',
        loadErrorDesc: 'Please try again later.',
        emptyTitle: 'No data in this period',
        emptyDesc: 'Adjust period or filters.',
        time: 'Time',
        csvPrefix: 'report',
        noGroup: '— unassigned —',
    },
    clients: {
        newClient: '+ New client',
        emptyTitle: 'No clients yet',
        emptyDesc:
            'Create your first client to manage projects and billable hours.',
        deleteConfirm:
            'Really delete client “{name}”? Assigned projects will remain.',
        deleted: 'Client deleted',
        projectsCount: 'Projects ({count})',
        newProject: '+ Project',
        noProjects: 'No projects for this client yet.',
        editProjectTooltip: 'Edit project',
        noRate: 'no rate',
        name: 'Name',
        contactDetails: 'Contact details',
        contactPlaceholder: 'Email, phone, contact person …',
        notes: 'Notes',
        invoiceAddress: 'Billing address',
        addressPlaceholder: 'Street, postal code, city, country',
        vatLabel: 'VAT ID (optional)',
        vatPlaceholder: 'ATU…',
        createTitle: 'New client',
        editTitle: 'Edit client',
        created: 'Client created',
        saved: 'Client saved',
        invalidRate: 'Invalid hourly rate.',
        projectFor: 'Project for {name}',
        editProjectTitle: 'Edit project',
        hourlyRateLabel: 'Hourly rate (€, empty = none)',
        ratePlaceholder: 'e.g. 65',
        projectCreated: 'Project created',
        projectSaved: 'Project saved',
        projectDeleted: 'Project deleted',
        projectDeleteConfirm: 'Really delete project “{name}”?',
    },
    invoices: {
        title: 'Invoices',
        new: '+ New invoice',
        emptyTitle: 'No invoices',
        emptyDesc: 'Create an invoice from your unbilled, billable hours.',
        number: 'Number',
        client: 'Client',
        issueDate: 'Issue date',
        status: 'Status',
        sum: 'Total',
        pdf: 'PDF',
        pdfName: 'Invoice_{number}.pdf',
        pdfFailed: 'Could not load PDF',
        pageInfo: 'Page {page} of {totalPages} · {count} invoices',
        detailTitle: 'Invoice {number}',
        due: 'Due',
        servicePeriod: 'Service period',
        taxRate: 'Tax rate',
        position: 'Item',
        hours: 'Hours',
        rate: 'Rate',
        amount: 'Amount',
        net: 'Net',
        vat: 'VAT ({rate} %)',
        gross: 'Gross',
        downloadPdf: '↓ Download PDF',
        markSent: 'Mark as sent',
        markPaid: 'Mark as paid',
        cancelInvoice: 'Cancel invoice',
        setStatusConfirm: 'Set invoice to “{label}”?',
        statusChanged: 'Status changed: {label}',
        updated: 'Invoice updated',
        editTitle: 'Edit {number}',
        dueOptional: 'Due date (optional)',
        vatPct: 'VAT %',
        noteOptional: 'Note (optional)',
        notePlaceholder: 'e.g. payment terms',
        draftHint:
            'Only drafts can be edited. VAT and totals are recalculated automatically.',
        createTitle: 'Create invoice',
        createSuccess: 'Invoice {number} has been created',
        createSuccessDesc: 'The billable hours are now assigned to this invoice.',
        chooseClient: '— Select client —',
        serviceFrom: 'Service period from (optional)',
        serviceTo: 'Service period to (optional)',
        unbilled: 'Unbilled hours',
        unbilledFrom: 'from {date}',
        unbilledUntil: 'until {date}',
        loadingPreview: 'Loading preview …',
        noProject: 'No project',
        netEntries: 'Net ({count} entries)',
        skippedWithoutRate:
            '{count} billable entries without an hourly rate are not invoiced.',
        noUnbilled: 'No open billable hours for this client.',
        create: 'Create invoice',
    },
    billing: {
        title: 'Billing',
        subtitle: 'Time entries, reports and invoices of your work.',
    },
    workingTime: {
        prevMonth: 'Previous month',
        nextMonth: 'Next month',
        member: 'Member',
        statusOpen: 'Open',
        statusClosed: 'Closed',
        csvExport: 'CSV',
        csvName: 'monthly-hours',
        loadErrorTitle: 'Working time could not be loaded',
        setupTitle: 'Working time setup',
        setupDescription:
            'Start date, core time window, weekly target hours and vacation days per year are required before the monthly table can be calculated.',
        setupStart: 'Start date',
        setupCoreFrom: 'Core time from',
        setupCoreTo: 'Core time to',
        setupWeeklyHours: 'Weekly target hours',
        setupVacationDays: 'Vacation days per year',
        setupIncomplete: 'Please fill in all required fields',
        setupCoreTimeInvalid:
            'Invalid core time window: "from" must be before "to"',
        setupSaved: 'Working time setup saved',
        setupWeeklyHoursShort: 'Target h/week',
        setupVacationDaysShort: 'Vacation/year',
        contractSaved: 'Contract data saved',
        autoBreakLabel: 'Automatic lunch break',
        autoBreakHint: 'From 6 h of recorded duty time, 0.5 h is deducted (evaluation only).',
        overtimeCarryLabel: 'Overtime carry-over (h)',
        vacationCarryLabel: 'Vacation carry-over (days)',
        carryHint: 'Carry-overs feed into the annual accounts (manual transfer model).',
        sundayHolidayLabel: 'Sunday = default holiday',
        holidayFactorSettingsLabel: 'Holiday factor',
        tiersTitle: 'Overtime tiers',
        tiersHint:
            'Factor from a daily hour — e.g. "from 8 → 1.0" and "from 10 → 1.5". Hours below the first tier count × 1.',
        tierFrom: 'From hour',
        tierFactor: 'Factor',
        tierAdd: '+ Add tier',
        absenceTitle: 'Record absence',
        absenceType: 'Type',
        absenceDate: 'Date',
        absenceFullDay: 'Full day',
        absenceFullDayHint: 'Records the daily target ({hours} h).',
        absenceFullDayNoSetup:
            'Full day requires the working time setup first (weekly target unknown).',
        absenceHalfDay: 'Half day',
        absenceHalfDayHint:
            'Records half the daily target ({hours} h) — consumes half a vacation day.',
        absenceHalfDayPlainHint:
            'Records half the daily target ({hours} h).',
        absenceSickHint:
            'Sick leave is recorded in half or full days and covers the daily target like vacation.',
        absenceVacationHint:
            'Vacation is consumed in half or full days only; work fills the target first.',
        absencePaidHint:
            'Paid absence (e.g. doctor, public authority) counts towards actual hours — booked hourly.',
        absenceFrom: 'From',
        absenceTo: 'To',
        absenceTimeInvalid: '"To" must be after "From"',
        absenceRemaining: 'Still bookable up to the daily target: {hours}',
        absenceExceedsTarget:
            'This would push actual hours beyond the target — at most {hours} left to book.',
        absenceExisting: 'Existing absences on this day',
        absenceDeleted: 'Absence deleted',
        absenceSave: 'Record',
        absenceSaved: 'Absence recorded',
        absenceHint:
            'Never billable. Work fills the target first — absence only covers the remaining gap.',
        absenceRecord: 'Record {type}',
        absenceAdd: 'Record absence',
        addWork: 'Log work',
        showDayEntries: 'Show entries of this day',
        hideDayEntries: 'Hide entries of this day',
        holidayMarked: 'Marked as holiday',
        holidaySaved: 'Holiday saved',
        holidayRemoved: 'Holiday mark removed',
        holidayEdit: 'Edit holiday',
        holidayMark: 'Mark as holiday',
        holidayFactorLabel: 'Factor (empty = default {default})',
        holidayNoteLabel: 'Note (optional)',
        holidayRemove: 'Remove mark',
        importMonth: 'Adopt month',
        importedMonth: 'Month state adopted',
        importWeek: 'Adopt',
        importedWeek: 'Week state adopted',
        colActions: 'Actions',
        colNight: 'of which night',
        nightFromLabel: 'Night window from',
        nightToLabel: 'Night window to',
        nightFactorLabel: 'Night factor',
        nightHint:
            'Entries are split at the window edge during evaluation; the factor applies to crediting night hours.',
        closeMonth: 'Close month',
        closeConfirm:
            'Close month {month}? The day rows are frozen into a snapshot the PDF record is built from.',
        closedToast: 'Month closed',
        reopenMonth: 'Reopen',
        reopenConfirm:
            'Reopen month {month}? The snapshot is deleted and recalculated live — a new close requires a new signature.',
        reopenedToast: 'Month reopened — live calculation active',
        closedInfo:
            'Closed on {date} — rows frozen from the snapshot; changes only via “Reopen”.',
        pdfExport: 'PDF record',
        pdfName: 'working-time-record',
        kpiVacation: 'Remaining vacation (year)',
        kpiVacationHint: 'Entitlement {entitlement} − used {used}',
        kpiOvertime: 'Overtime balance',
        kpiOvertimeHint: 'Credit {credit} h − time off {used} h',
        kpiMonthSaldo: 'Month balance',
        kpiMonthSaldoHint: 'Target {target} · worked {worked}',
        weekLabel: 'CW',
        weekSum: 'Week',
        monthTotal: 'Month total',
        colDate: 'Day',
        colTarget: 'Target',
        colWorked: 'Worked',
        colBreak: 'Break',
        colAbsence: 'Absent',
        colSaldo: 'Balance',
        holiday: 'Holiday',
        absenceSickShort: 'S',
        absenceVacationShort: 'V',
        absenceTimeOffShort: 'TO',
    },
    workspace: {
        title: 'Workspace',
        subtitle: 'Manage clients, projects, members and addresses.',
        tabClients: 'Clients',
        tabGeneral: 'General',
        tabMembers: 'Members',
        tabAddresses: 'Addresses',
        billingData: 'Billing data',
        wsName: 'Workspace name',
        vatLabel: 'VAT ID (§ 11 UStG, optional)',
        vatPlaceholder: 'e.g. ATU12345678',
        vatHint: 'The VAT ID appears on the invoice PDF.',
        updated: 'Workspace updated',
        workingTimeTitle: 'Working time settings',
        workingTimeWorkdays: 'Workdays',
        workingTimeWorkdaysHint:
            'Days on which the weekly target applies (daily target = weekly target ÷ workdays).',
        workingTimeSaved: 'Working time settings saved',
        contract: 'Working time',
        members: 'Members',
        noMembers: 'No members',
        name: 'Name',
        email: 'Email',
        role: 'Role',
        rateLabel: 'Hourly rate (€/h)',
        you: '(you)',
        leave: 'Leave',
        remove: 'Remove',
        leaveConfirm: 'Really leave this workspace?',
        removeConfirm: 'Remove {name} from the workspace?',
        leftToast: 'Left the workspace',
        removedToast: '{name} removed',
        promoteConfirm: 'Promote {name} to owner?',
        promote: 'Promote',
        roleChanged: 'Role of {name}: {role}',
        rateSaved: 'Hourly rate saved',
        inviteTitle: 'Invite member',
        roleMember: 'Member',
        roleAdmin: 'Admin',
        invite: 'Invite',
        inviteHint:
            'Unknown email addresses receive an invitation by email (valid for 7 days).',
        inviteEmailPlaceholder: 'colleague@company.com',
        addedDirect: '{email} has been added directly.',
        inviteSent: 'Invitation sent to {email} (valid for 7 days).',
        openInvites: 'Open invitations',
        noInvites: 'No open invitations',
        noInvitesDesc:
            'Sent invitations appear here until accepted, expired or revoked.',
        validUntil: 'Valid until',
        invitedBy: 'Invited by',
        revoke: 'Revoke',
        revokeConfirm: 'Revoke invitation to {email}?',
        revoked: 'Invitation revoked',
        roles: {
            owner: 'Owner',
            admin: 'Admin',
            member: 'Member',
        },
        addressesInfoBadge: 'Info',
        addressesInfo: 'The default address appears as the sender on the invoice PDF.',
        addresses: 'Addresses',
        newAddress: '+ New address',
        noAddresses: 'No addresses yet',
        noAddressesManage:
            'Create an address to be used as the sender on invoice PDFs.',
        noAddressesNoManage: 'Only owners and admins can create addresses.',
        addressCol: 'Address',
        countryCol: 'Country',
        defaultBadge: 'Default',
        addressDeleteConfirm: 'Delete address {street} {number}, {zip} {city}?',
        addressDeleted: 'Address deleted',
        addressCreated: 'Address created',
        addressUpdated: 'Address updated',
        createAddressTitle: 'New address',
        editAddressTitle: 'Edit address',
        street: 'Street',
        streetNumber: 'Number',
        additionalInfo: 'Additional info (optional)',
        additionalInfoPlaceholder: 'Floor, door, …',
        postalCode: 'Postal code',
        city: 'City',
        country: 'Country',
        countryDefault: 'Austria',
        setDefault: 'Set as default address (replaces the current one)',
        dangerTitle: 'Delete workspace',
        dangerDesc:
            'The workspace will be deleted permanently along with all its data: members, clients & projects, time entries, invoices, addresses and working-time data. This cannot be undone.',
        nameConfirmLabel: 'Type the workspace name to confirm',
        requestCode: 'Request deletion code',
        requestCodeAgain: 'Send code again',
        codeSent:
            'Deletion code sent to {email} (valid for 15 minutes, single use).',
        codeLabel: 'Deletion code (6 digits)',
        codePlaceholder: 'e.g. 418372',
        deleteButton: 'Delete workspace permanently',
        deleteConfirm:
            'Really delete workspace "{name}" permanently? All associated data will be removed irreversibly.',
        deleted: 'Workspace deleted',
    },
    settings: {
        title: 'Settings',
        subtitle: 'Profile, password, email and account management.',
        profile: 'Profile',
        name: 'Name',
        language: 'Language',
        languageDe: 'Deutsch',
        languageEn: 'English',
        profileSaved: 'Profile saved',
        verifyTitle: 'Verify email',
        verifyDesc:
            'Your email address is not verified yet. Follow the link in the verification email.',
        resend: 'Resend',
        resent: 'Verification email resent',
        passwordTitle: 'Change password',
        currentPassword: 'Current password',
        newPassword: 'New password',
        newPasswordRepeat: 'Repeat new password',
        passwordMismatch: 'The new passwords do not match.',
        passwordHint:
            '8–128 characters, upper and lower case letters, number and special character. You will be signed out after changing — all sessions become invalid.',
        passwordChanged: 'Password changed — please sign in again',
        emailTitle: 'Change email address',
        current: 'Current:',
        pendingBadge: 'Pending',
        pendingFor: 'Pending confirmation for {email}',
        newEmail: 'New email',
        requestChange: 'Request change',
        changeSent:
            'Confirmation email sent to {email} — only the link there completes the change.',
        dangerTitle: 'Delete account',
        dangerDesc:
            'The account including all workspaces, time entries and invoices will be deleted. Enter your email address to request a confirmation link.',
        confirmEmailLabel: 'Confirm email ({email})',
        requestDelete: 'Request deletion',
        deleteRequested:
            'The confirmation link has been emailed. Deletion is only performed after clicking the link in the email.',
        deleteLinkSent: 'The confirmation link has been emailed.',
    },
    admin: {
        title: 'Admin',
        subtitle: 'Platform statistics, pageviews and user management.',
        unavailable: 'Statistics unavailable',
        unavailableDesc: 'No admin access or backend unreachable.',
        kpiUsers: 'Users',
        kpiVerified: 'Verified',
        kpiUnverified: 'Unverified',
        kpiWorkspaces: 'Workspaces',
        kpiActive30: 'Active 30 d',
        kpiTracked30: 'Tracked 30 d',
        registrationsTitle: 'Registrations / day (90 days)',
        registrationsLabel: 'registrations',
        activeTitle: 'Active users / day (30 days)',
        activeLabel: 'active users',
        invoicesByStatus: 'Invoices by status',
        noInvoices: 'No invoices yet.',
        pageviewsTitle: 'Pageviews (top 10, 30 days)',
        noPageviews: 'No pageviews',
        path: 'Path',
        views: 'Views',
        usersTitle: 'Users',
        userSearch: 'Search name or email …',
        noUsers: 'No users found',
        noUsersDesc: 'Adjust your search.',
        registered: 'Registered',
        languageCol: 'Language',
        lastActive: 'Last active',
        workspacesCol: 'Workspaces',
        adminBadge: 'Admin',
        notVerified: 'unverified',
        usersOf: '{shown} of {total} users',
        loadMore: 'Load more',
        // Email log (operator)
        emailLogTitle: 'Email log',
        emailLogSearch: 'Search recipient or subject …',
        emailFilterAll: 'All statuses',
        emailTypeAll: 'All types',
        noEmailLogs: 'No emails logged',
        noEmailLogsDesc:
            'Every sent or failed email shows up here.',
        emailLogsOf: '{shown} of {total} entries',
        emailColTime: 'Time',
        emailColType: 'Type',
        emailColRecipient: 'Recipient',
        emailColSubject: 'Subject',
        emailColStatus: 'Status',
        emailStatusSent: 'Sent',
        emailStatusFailed: 'Failed',
        emailTypeVerification: 'Verification',
        emailTypeChange: 'Email change',
        emailTypeChangeNotice: 'Email change notice',
        emailTypePasswordReset: 'Password reset',
        emailTypeDeleteUser: 'Account deletion',
        emailTypeDeleteWorkspace: 'Workspace deletion',
        emailTypeInvite: 'Workspace invite',
        emailTypeAdded: 'Workspace added',
        emailTypeInvoice: 'Invoice',
        emailTypeDunning: 'Dunning',
        emailTypeTest: 'Test email',
        emailTypeAdminTemplate: 'Admin template mail',
        emailTypeAdminFree: 'Admin free-text mail',
        // Settings (operator)
        settingsTitle: 'Settings',
        settingsUnavailable: 'Settings unavailable',
        signupRow: 'Public registration',
        stateEnabled: 'Enabled',
        stateDisabled: 'Disabled',
        signupDesc:
            'Controls whether new users can register. Existing accounts and sign-in are not affected.',
        activateSignup: 'Enable',
        deactivateSignup: 'Disable',
        confirmActivate:
            'Enable registration again? New users will be able to sign up afterwards.',
        confirmDeactivate:
            'Disable registration? New users will no longer be able to sign up; existing accounts are not affected.',
        signupActivated: 'Registration enabled.',
        signupDeactivated: 'Registration disabled.',
        testMailRow: 'Test email',
        testMailDesc:
            'Checks mail delivery via the configured SMTP connection.',
        testMailTo: 'Recipient',
        sendTestMail: 'Send test email',
        testMailSent: 'Test email sent.',
        // Send email (templates in all languages + free text)
        sendMailTitle: 'Send email',
        sendMailDesc:
            'Send any mail template in German or English as a preview with sample data — or a mail with free text and subject.',
        sendMailModeTemplate: 'Template',
        sendMailModeFree: 'Free text',
        sendMailTemplate: 'Template',
        sendMailLanguage: 'Language',
        sendMailTo: 'Recipient',
        sendMailSubject: 'Subject',
        sendMailText: 'Message',
        sendMailTextPlaceholder: 'Email content …',
        sendMailButton: 'Send email',
        sendMailSent: 'Email sent.',
        sendMailNoAttachmentHint:
            'Invoice and dunning templates are sent without a PDF attachment.',
    },
    feedback: {
        title: 'Give feedback',
        intro: 'What can we do better? Your email address and the current page are sent automatically.',
        category: 'Category',
        message: 'Message',
        messagePlaceholder: 'Describe your concern …',
        currentPage: 'Current page',
        sentAs: 'Sent as',
        send: 'Send',
        sent: 'Thanks! Your feedback has been received.',
        catBug: 'Bug',
        catImprovement: 'Improvement suggestion',
        catFeatureRequest: 'Feature request',
        catOther: 'Other inquiry',
        statusOpen: 'Open',
        statusRead: 'Read',
        statusDone: 'Done',
        // Admin panel
        adminTitle: 'Feedback',
        filterAll: 'All',
        noFeedback: 'No feedback',
        noFeedbackDesc: 'No entries match this filter.',
        colDate: 'Date',
        colCategory: 'Category',
        colUser: 'User',
        colMessage: 'Message',
        colPath: 'Page',
        colStatus: 'Status',
        markRead: 'Mark read',
        markDone: 'Done',
        reopen: 'Reopen',
        entriesOf: '{shown} of {total} entries',
        // Detail dialog (admin)
        detailTitle: 'Feedback details',
        showFull: 'Click to show the full message',
    },
    login: {
        signin: 'Sign in',
        signup: 'Sign up',
        name: 'Name',
        email: 'Email',
        password: 'Password',
        passwordHint:
            'At least 8 characters with upper and lower case letters, a number and a special character. You will receive a verification email after signing up.',
        forgot: 'Forgot password?',
        createAccount: 'Create account',
        unexpected: 'Unexpected error — is the backend reachable?',
        signupDisabled: 'Registration is currently disabled.',
        signupDisabledInvite: ' This invitation cannot be accepted.',
        inviteHint:
            'You have been invited — sign up with the invited email address to join the workspace.',
        inviteMismatch:
            'Invitation not accepted: the email address does not match the invitation.',
        betaHint:
            'Optimus Time is in beta — not yet intended for production use.',
    },
    reset: {
        title: 'Reset password',
        sent:
            'If the account exists, an email with the reset link has been sent. Check your inbox.',
        send: 'Send reset link',
        newTitle: 'Set new password',
        mismatch: 'The passwords do not match.',
        hint: '8–128 characters, upper and lower case letters, number and special character.',
        save: 'Save password',
        savedToast: 'Password set — please sign in again',
        backToLogin: '← Back to sign in',
    },
    verify: {
        invalidTitle: 'Invalid link',
        noToken: 'The verification link does not contain a token.',
        verifying: 'Verifying email …',
        successTitle: 'Email address verified',
        errorTitle: 'Verification failed',
        errorDesc:
            'The link is invalid or has already been used. Request a new email in the settings.',
        toSettings: 'Go to settings',
        toLogin: 'Go to sign in',
    },
    emailChange: {
        invalidTitle: 'Invalid link',
        noToken: 'The link is missing a token.',
        confirming: 'Confirming email change …',
        successTitle: 'Email change confirmed',
        errorTitle: 'Confirmation failed',
        errorDesc:
            'The link is invalid, expired or the new address is already in use.',
        toSettings: 'Go to settings',
        toLogin: 'Go to sign in',
    },
    deleteAccount: {
        invalidTitle: 'Invalid link',
        noToken: 'The deletion link is missing a token.',
        title: 'Delete account permanently',
        desc:
            'This action deletes the account including all workspaces, time entries and invoices and cannot be undone. Confirm with your email address.',
        confirmEmail: 'Confirm email',
        button: 'Delete account permanently',
        deleted: 'Account deleted.',
    },
    onboarding: {
        welcome: 'Welcome to Optimus Time',
        desc: 'Create your first workspace to manage clients, projects and time.',
        name: 'Workspace name',
        placeholder: 'e.g. Freelance',
        created: 'Workspace created',
        createFailed: 'Error creating workspace',
    },
    charts: {
        barChartAria: 'Bar chart: {label} per day',
    },
    theme: {
        label: 'Appearance',
        toLight: 'Switch to light mode',
        toDark: 'Switch to dark mode',
    },
    errors: {
        unexpected: 'Unexpected error',
    },
    footer: {
        tagline: 'Simple time tracking for you and your team.',
        betaNote: 'In beta — not yet intended for production use.',
        product: 'Product',
        legalTitle: 'Legal',
        impressum: 'Imprint',
        datenschutz: 'Privacy Policy',
        agb: 'Terms and Conditions',
        home: 'Home',
    },
    legal: {
        noteTemplate:
            'Note: this legal text is a placeholder template — it must be reviewed and completed by the operator.',
        noteBeta:
            'In addition, Optimus Time is in beta; production use is not yet intended.',
        impressum: {
            title: 'Imprint',
            mediaholderH: 'Media owner and service provider (§ 5 ECG, § 25 MedienG)',
            mediaholder: '[TODO: full name or company name]',
            address:
                '[TODO: street and number]\n[TODO: postal code and city]\n[TODO: country]',
            contactH: 'Contact',
            emailLabel: 'E-mail',
            businessH: 'Business information',
            business:
                'Field of activity: [TODO: e.g. software development / services]',
            vat: 'VAT-ID (UID): [TODO: if applicable]',
            authority: 'Supervisory authority: [TODO: if applicable]',
            membership: 'Chamber membership: [TODO: if applicable]',
            odrH: 'EU online dispute resolution',
            odr: 'The European Commission provides a platform for online dispute resolution (ODR): https://ec.europa.eu/consumers/odr. We are neither obliged nor willing to participate in dispute resolution proceedings before a consumer arbitration board.',
        },
        datenschutz: {
            title: 'Privacy Policy',
            controllerH: 'Controller',
            controller:
                '[TODO: full name / company name]\n[TODO: street and number]\n[TODO: postal code, city, country]',
            controllerContact: 'E-mail: hallo@optimustime.at',
            dataH: 'What data do we process?',
            dataIntro:
                'When you use Optimus Time, we process the following data:',
            dataAccount:
                'Account data: name, e-mail address, password (stored only as a hash)',
            dataTracking:
                'Time tracking data: descriptions, project and client assignments, start and end times, billable flags and the settings you create',
            dataTechnical:
                'Technical data: log data and authentication tokens required to operate the service',
            cookiesH: 'Cookies and local storage',
            cookiesIntro:
                'This website only uses technically necessary cookies and local storage (§ 165 (3) no. 3 TKG 2021). There are no analytics, advertising or other tracking services — therefore no cookie consent is required.',
            cookiesSession:
                'refresh_token (cookie, 7 days): keeps you signed in across page reloads; httpOnly and therefore protected from script access.',
            cookiesLocale:
                'Language choice (kept in the local state store): remembers the interface language you last selected.',
            cookiesStorage:
                'Local storage (localStorage): display theme and the timer description draft. This data never leaves your device.',
            cookiesFuture:
                'Should consent-requiring services (e.g. usage statistics) be added in the future, we will ask for your consent beforehand.',
            reachH: 'Reach measurement',
            reachText:
                'For internal statistics we count page views in anonymised form: the path of the visited page and the date are stored as an aggregated counter — without cookies, without user IDs and without IP addresses. This data cannot be linked to individuals; no consent is therefore required (§ 165 (3) TKG 2021).',
            purposeH: 'Purposes and legal basis',
            purposeText:
                'We process your data in order to provide the contracted service (Art 6 (1) (b) GDPR), to comply with legal obligations (Art 6 (1) (c) GDPR) and to ensure secure operation on the basis of our legitimate interest (Art 6 (1) (f) GDPR).',
            hostingH: 'Hosting',
            hostingText:
                'The service is hosted on servers within the European Union. [TODO: hosting provider and, if applicable, data processing agreement details]',
            emailH: 'E-mail communication',
            emailText:
                'We use your e-mail address for account-related communication (registration, verification, invitations, password and e-mail changes). No newsletters are sent.',
            retentionH: 'Retention and deletion',
            retentionText:
                'Your data is stored for as long as your account exists. When you delete your account, your personal data is deleted as well, unless we are legally required to retain it.',
            rightsH: 'Your rights',
            rightsText:
                'You have the right to information, rectification and erasure of your data as well as restriction of processing, data portability and objection (Art 15–21 GDPR). You also have the right to lodge a complaint with the Austrian Data Protection Authority (www.dsb.gv.at).',
            changesH: 'Changes to this policy',
            changesText:
                'We may adapt this privacy policy when the service changes. The current version is always available on this page. [TODO: date of last update]',
        },
        agb: {
            title: 'Terms and Conditions',
            s1H: '§ 1 Scope',
            s1T: 'These terms and conditions govern the use of the Optimus Time time tracking service at optimustime.at, operated by [TODO: operator name and address].',
            s2H: '§ 2 Account and registration',
            s2T: 'Use of the service requires a user account with a valid e-mail address, which must be verified. Users must be of legal age or have the consent of a legal guardian. [TODO: minimum age if needed]',
            s3H: '§ 3 Obligations of the user',
            s3T: 'You are responsible for the accuracy of your data and for keeping your access credentials confidential. Any use of the service that violates applicable law, in particular the use of third-party data without authorization, is prohibited.',
            s4H: '§ 4 Availability',
            s4T: 'The service is provided with the customary care. There is no guarantee of uninterrupted availability; maintenance work may lead to temporary interruptions. [TODO: availability commitments, if any]',
            s5H: '§ 5 Workspaces and invitations',
            s5T: 'Users collaborate in workspaces with different roles (owner, admin, member). Those who invite other users are responsible for being entitled to use the invited e-mail addresses.',
            s6H: '§ 6 Liability',
            s6T: 'Liability for slight negligence is excluded, except for personal injury or claims under the Austrian Product Liability Act. [TODO: liability clauses to be reviewed]',
            s7H: '§ 7 Term and termination',
            s7T: 'The account can be terminated at any time. Deletion is triggered in the settings via a confirmation link sent by e-mail.',
            s8H: '§ 8 Final provisions',
            s8T: 'Austrian law applies. The place of jurisdiction is [TODO: place of jurisdiction]. Should individual provisions of these terms be invalid, the remaining provisions shall remain in force.',
        },
    },
}
